import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Navigation from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PricingConfigEditor } from '@/components/admin/PricingConfigEditor';
import { AdminAnalytics } from '@/components/admin/AdminAnalytics';
import { ProductsTable } from '@/components/admin/ProductsTable';
import { ContentGenerator } from '@/components/admin/ContentGenerator';
import { ProductComparisonGenerator } from '@/components/admin/ProductComparisonGenerator';
import { AgentManagement } from '@/components/admin/AgentManagement';
import { AIAutomationSettings } from '@/components/admin/AIAutomationSettings';
import { MaterialsQuotesPanel } from '@/components/admin/MaterialsQuotesPanel';
import { SEO } from '@/components/SEO';
import { toast } from 'sonner';

interface Lead {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  interest_type: string | null;
  budget_range: string | null;
  location: string | null;
  message: string | null;
  status: string | null;
  qualification_score: number | null;
  created_at: string;
}

interface Product {
  id: string;
  agent_id: string;
  name: string;
  model: string | null;
  description: string | null;
  unit_price: number;
  quantity: number;
  total_price: number | null;
  status: string;
  rejection_reason: string | null;
  seo_score: number | null;
  seo_keywords: string[] | null;
  created_at: string;
}

const Admin = () => {
  const { user, signOut, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const TAB_STORAGE_KEY = 'aureum_admin_active_tab';
  const VALID_TABS = ['analytics', 'agents', 'products', 'content', 'comparison', 'leads', 'materials', 'pricing', 'ai'];
  const initialTab = (() => {
    const fromUrl = searchParams.get('tab');
    if (fromUrl && VALID_TABS.includes(fromUrl)) return fromUrl;
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(TAB_STORAGE_KEY);
      if (stored && VALID_TABS.includes(stored)) return stored;
    }
    return 'analytics';
  })();
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  useEffect(() => {
    localStorage.setItem(TAB_STORAGE_KEY, activeTab);
    if (searchParams.get('tab') !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const [leads, setLeads] = useState<Lead[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !roleLoading) {
      if (!user) {
        navigate('/auth');
      } else if (!isAdmin()) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
      }
    }
  }, [user, authLoading, roleLoading, isAdmin, navigate]);

  useEffect(() => {
    if (user) {
      fetchLeads();
      fetchProducts();
    }
  }, [user]);

  const fetchLeads = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Failed to fetch leads');
    } else {
      setLeads(data || []);
    }
    setLoading(false);
  };

  const fetchProducts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Failed to fetch products');
    } else {
      setProducts(data || []);
    }
    setLoading(false);
  };

  const handleApproveProduct = async (productId: string) => {
    const { error } = await supabase
      .from('products')
      .update({ 
        status: 'approved',
        approved_at: new Date().toISOString(),
        approved_by: user?.id,
        rejection_reason: null
      })
      .eq('id', productId);

    if (error) {
      toast.error('Failed to approve product');
    } else {
      toast.success('Product approved');
      fetchProducts();
    }
  };

  const handleRejectProduct = async (productId: string) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    const { error } = await supabase
      .from('products')
      .update({ 
        status: 'rejected',
        rejection_reason: reason
      })
      .eq('id', productId);

    if (error) {
      toast.error('Failed to reject product');
    } else {
      toast.success('Product rejected');
      fetchProducts();
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  if (authLoading || roleLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Admin Dashboard | Aureum VII Trading"
        description="Manage leads and customer inquiries"
        type="website"
        noindex
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
              <p className="text-muted-foreground">Signed in as: {user.email}</p>
            </div>
            <Button onClick={handleSignOut} variant="outline">
              Sign Out
            </Button>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="agents">Agent Management</TabsTrigger>
              <TabsTrigger value="products">Product Approvals</TabsTrigger>
              <TabsTrigger value="content">Content Generator</TabsTrigger>
              <TabsTrigger value="comparison">Product Comparison</TabsTrigger>
              <TabsTrigger value="leads">Leads</TabsTrigger>
              <TabsTrigger value="materials">Materials Quotes</TabsTrigger>
              <TabsTrigger value="pricing">Pricing Config</TabsTrigger>
              <TabsTrigger value="ai">AI Automation</TabsTrigger>
            </TabsList>

            <TabsContent value="analytics">
              {loading ? (
                <p className="text-center py-8 text-muted-foreground">Loading analytics...</p>
              ) : (
                <AdminAnalytics products={products} />
              )}
            </TabsContent>

            <TabsContent value="agents">
              <AgentManagement />
            </TabsContent>

            <TabsContent value="products">
              {loading ? (
                <p className="text-center py-8 text-muted-foreground">Loading products...</p>
              ) : (
                <ProductsTable
                  products={products}
                  onApprove={handleApproveProduct}
                  onReject={handleRejectProduct}
                  onRefresh={fetchProducts}
                />
              )}
            </TabsContent>

            <TabsContent value="content">
              <ContentGenerator />
            </TabsContent>

            <TabsContent value="comparison">
              <ProductComparisonGenerator />
            </TabsContent>

            <TabsContent value="leads">
            <Card>
              <CardHeader>
                <CardTitle>Recent Leads</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8 text-muted-foreground">Loading leads...</p>
                ) : leads.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No leads found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Interest</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Score</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leads.map((lead) => (
                          <TableRow key={lead.id}>
                            <TableCell className="text-sm">
                              {new Date(lead.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="font-medium">{lead.name || '-'}</TableCell>
                            <TableCell>{lead.email || '-'}</TableCell>
                            <TableCell>{lead.company || '-'}</TableCell>
                            <TableCell>{lead.interest_type || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={lead.status === 'qualified' ? 'default' : 'secondary'}>
                                {lead.status || 'new'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{lead.qualification_score || 0}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
            </TabsContent>

            <TabsContent value="materials">
              <MaterialsQuotesPanel />
            </TabsContent>

            <TabsContent value="pricing">
              <PricingConfigEditor />
            </TabsContent>

            <TabsContent value="ai">
              <AIAutomationSettings />
            </TabsContent>
          </Tabs>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default Admin;
